<template>
  <header class="app-header" :class="[
      {'app-header--active': showMobileMenu},
      {'app-header--scrolled': isScrolled},
      `app-header--${headerTheme}`
    ]">
    <!-- Logo部分（PC和Mobile共用） -->
    <a class="app-header__logo" :href="getNavLink('/')">
      <img :src="domainConfig?.['siteIcon']" class="app-header__logo-icon" alt="site logo">
      <div class="app-header__logo-text" :class="[
          {'app-header__logo-text--active': showMobileMenu},
        ]">
        {{domainConfig?.['siteName']}}</div>
    </a>

    <!-- PC导航菜单 (只在桌面设备显示) -->
    <nav class="app-header__nav" v-if="deviceType === 'desktop' || deviceType === 'pc'">
      <a v-for="(item, index) in navItems" :key="index" :href="getNavLink(item.link)"
        class="app-header__nav-item" :class="[
          {'app-header__nav-item--active': isRouteActive(item.link)},
          `app-header__nav-item--${headerTheme}`,
          {'app-header__nav-item--scrolled': isScrolled}
        ]">
        {{ item.name }}
      </a>
    </nav>

    <!-- Mobile菜单按钮 (只在移动设备显示) -->
    <div class="app-header__mobile-trigger" @click="toggleMobileMenu">
      <img v-if="!showMobileMenu" :src="menuSrc" alt="menu">
      <img v-else src="@/assets/clos-menu.svg" alt="close menu">
    </div>

    <!-- Mobile菜单 (显示/隐藏取决于showMobileMenu) -->
    <div class="app-header__mobile-menu" v-if="showMobileMenu">
      <div class="app-header__mobile-menu-items">
        <a v-for="(item, index) in navItems" :key="index" :href="getNavLink(item.link)"
          class="app-header__mobile-menu-item" :class="[
            {'app-header__mobile-menu-item--active': isRouteActive(item.link)},
            `app-header__mobile-menu-item--${headerTheme}`
          ]" @click="showMobileMenu = false">
          {{ item.name }}
          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10"
            fill="none">
            <path d="M1.54175 1.5L5.04175 5L1.54175 8.5" stroke="#050B20" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
      </div>
    </div>
  </header>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AppHeader',
  props: {
    headerTheme: {
      type: String,
      default: 'dark' // 可选值：'light'或'dark'
    },
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      domainConfig: 'domainConfig'
    }),
    menuSrc () {
      return this.headerTheme === 'dark'
        ? require('@/assets/mobile-menu-dark.svg')
        : require('@/assets/mobile-menu.svg');
    }
  },
  data () {
    return {
      showMobileMenu: false,
      isScrolled: false, // 新增：跟踪滚动状态
      scrollThreshold: 50, // 滚动阈值，超过该值会触发header样式变化
      navItems: [
        { name: 'Home', link: '/' },
        { name: 'All Cars', link: '/car-list' },
        { name: 'Rankings', link: '/car-rank' },
        { name: 'About Us', link: '/about' },
      ]
    };
  },
  methods: {
    toggleMobileMenu () {
      this.showMobileMenu = !this.showMobileMenu;

      // 如果打开菜单，禁止body滚动
      if (this.showMobileMenu) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    // 判断当前路由是否激活
    isRouteActive (link) {
      // 获取不包含channel参数的当前路径
      let currentPath = this.$route.path;
      const channel = this.$route.params.channel;

      // 如果存在channel参数，移除channel部分以便正确比较
      if (channel && currentPath.startsWith(`/${channel}`)) {
        currentPath = currentPath.replace(`/${channel}`, '') || '/';
      }

      // 处理根路径特殊情况
      if (link === '/' && (currentPath === '/' || currentPath === '')) {
        return true;
      }

      // 其他情况检查路径是否匹配（排除根路径情况）
      if (link !== '/') {
        return currentPath.startsWith(link);
      }

      return false;
    },

    // 获取导航链接
    getNavLink (link) {
      // 获取当前URL的查询参数
      const currentQuery = this.$route.query;
      const queryString = Object.keys(currentQuery).length > 0
        ? `?${new URLSearchParams(currentQuery).toString()}`
        : '';

      // 检查是否存在channel参数
      if (this.$route.params.channel) {
        // 如果是首页链接
        if (link === '/') {
          return `/${this.$route.params.channel}${queryString || ''}`;
        }
        // 其他链接
        return `/${this.$route.params.channel}${link}${queryString}`;
      }
      return `${link}${queryString}`;
    },

    // 处理滚动事件
    handleScroll () {
      this.isScrolled = window.scrollY > this.scrollThreshold;
    }
  },
  beforeDestroy () {
    // 确保在组件销毁时恢复body滚动
    document.body.style.overflow = '';
    // 移除滚动事件监听
    window.removeEventListener('scroll', this.handleScroll);
  },
  mounted () {
    // 监听路由变化，关闭移动菜单
    this.$router.afterEach(() => {
      if (this.showMobileMenu) {
        this.showMobileMenu = false;
        document.body.style.overflow = '';
      }
    });

    // 添加滚动事件监听
    window.addEventListener('scroll', this.handleScroll);
    // 初始化滚动状态
    this.handleScroll();
  }
};
</script>

<style lang="scss">
@import '~@/css/variables.scss';

// 使用命名空间避免样式冲突
.app-header {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	z-index: 100;
	display: flex;
	align-items: center;
	justify-content: space-between;
	box-sizing: border-box;
	height: rem(70);
	padding: 0 rem(120);
	transition: all 0.3s ease;

	// 默认透明背景
	&--light {
		background: transparent;
	}

	// 暗色主题
	&--dark {
		background: #fff;
	}

	// 滚动后变白色
	&--scrolled {
		background: #fff;
	}

	&__logo {
		display: flex;
		align-items: center;
		cursor: pointer;
		gap: rem(10);
		z-index: 100; // 确保在移动菜单上方
		flex: 1;
	}

	&__logo-icon {
		width: rem(36);
		height: rem(36);
		transition: all 0.3s ease;

		&--light {
			filter: brightness(0) invert(1); // 在light主题下使用滤镜使logo变为白色
		}

		&--dark {
			filter: none; // 在dark主题下显示原始颜色
		}
	}

	&__logo-text {
		font-size: rem(18);
		font-style: normal;
		font-weight: 900;
		line-height: rem(18); /* 100% */
		text-transform: uppercase;
		transition: color 0.3s ease;
		color: var(--theme-color) !important;

		&--light {
			color: #ffffff !important;
		}

		&--dark {
			color: var(--theme-color) !important;
		}

		&--active {
			color: var(--theme-color) !important;
		}
	}

	&__nav {
		display: flex;
		gap: rem(80);
	}

	&__nav-item {
		position: relative;
		font-size: rem(14);
		font-style: normal;
		font-weight: 400;
		line-height: rem(14); /* 100% */
		text-decoration: none;
		padding: rem(10) 0;
		transition: all 0.3s ease;

		// 激活状态显示橙色
		&--active {
			font-weight: 700;
			color: var(--theme-color) !important;
		}

		// 默认状态（未滚动）
		&--light {
			color: #141414;

			&:hover {
				opacity: 0.7; // hover时透明度减少30%
			}
		}

		// 暗色主题
		&--dark {
			color: #141414;

			&:hover {
				opacity: 0.7; // hover时透明度减少30%
			}
		}

		// 滚动后的状态
		&--scrolled {
			color: #141414;

			&:hover {
				opacity: 0.7; // hover时透明度减少30%
			}
		}
	}

	// 移动设备菜单部分样式
	&__mobile-trigger {
		width: rem(24);
		height: rem(24);
		display: none;
		cursor: pointer;
		z-index: 100;

		@media (max-width: 767px) {
			display: block;
		}
	}

	&__mobile-menu {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100vh;
		background: #ffffff;
		z-index: 99;
		display: flex;
		flex-direction: column;
		padding-top: rem(70);
		overflow-y: auto;
	}

	&__mobile-menu-items {
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: rem(10) rem(20);
	}

	&__mobile-menu-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: rem(16) 0;
		border-bottom: 1px solid #f3f3f3;
		text-decoration: none;
		color: #050b20;
		font-size: rem(16);
		font-weight: 400;

		&--active {
			font-weight: 700;
			color: var(--theme-color) !important;
		}
	}

	@media (max-width: 767px) {
		padding: 0 rem(20);

		&__nav {
			display: none;
		}
	}
}
</style>
